import React, { FC, useMemo, useRef } from 'react';
import { Vector } from '../../../../types';
import { Glyph } from '../../../../components/Glyph';
import { useFrame } from '@react-three/fiber';
import { repeat, multiply, add } from '../../../../utils';
import { ShipEntity, SwarmEntity } from './game';

export type ControlledGlyphProps = {
  matrix: number[][];
  colors: string[];
  indices: Vector;
  swarmEntity: SwarmEntity;
  shipEntity: ShipEntity;
};

const rand = (min: number = 0, max: number = 1, tilt: number = 1) => {
  return min + Math.pow(Math.random(), 1 / tilt) * (max - min);
};

export const randVector = () =>
  [rand(-75, 75), rand(0, 75), rand(-75, 75)] as Vector;

export const ControlledGlyph: FC<ControlledGlyphProps> = ({
  matrix,
  colors,
  indices,
  swarmEntity,
  shipEntity,
}) => {
  const s = useMemo(
    () => ({
      zPhase: rand(0, 2 * Math.PI),
      zRate: rand(3, 7),
      zAmt: rand(0.3, 0.9),
    }),
    [],
  );
  const directions = useMemo(() => repeat(100, randVector), []);
  const rotations = useMemo(() => repeat(100, randVector), []);
  const groupRef = useRef<any>();
  const pixelRefs = useRef<any>([]);
  const offset = useMemo(
    () => multiply(indices, swarmEntity.spacing),
    [indices, swarmEntity.spacing],
  );
  useFrame((state, delta) => {
    if (groupRef.current) {
      const exploding =
        swarmEntity.dead ||
        shipEntity.dead ||
        !swarmEntity.alive[indices[0]][indices[1]][indices[2]];
      if (exploding) {
        pixelRefs.current.forEach((pixel: any, i: number) => {
          if (pixel) {
            const direction = directions[i];
            const rotation = rotations[i];
            pixel.position.x += direction[0] * delta;
            pixel.position.y += direction[1] * delta;
            pixel.position.z += direction[2] * delta;
            pixel.rotation.x += rotation[0] * delta;
            pixel.rotation.y += rotation[1] * delta;
            pixel.rotation.z += rotation[2] * delta;
          }
        });
      } else {
        const [x, y, z] = add(swarmEntity.position, offset);
        groupRef.current.position.x = x;
        groupRef.current.position.y = y;
        groupRef.current.position.z =
          z +
          Math.sin(state.clock.elapsedTime * s.zRate + s.zPhase) * s.zAmt -
          5;
      }
    }
  });

  return (
    <Glyph
      matrix={matrix}
      colors={colors}
      position={offset}
      rotation={[Math.PI / 2, 0, 0]}
      groupRef={el => (groupRef.current = el)}
      pixelRef={(el, i) => (pixelRefs.current[i] = el)}
    />
  );
};
